import { ArrowLeft, Braces, Mail } from "lucide-react";
import * as React from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch, Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  LANGUAGE_NAME, MAX_NAME_CHARS, MERGE_FIELDS, asEmailSent, channelOf, emailTemplatePayload, emailTemplateProblem,
  fillText, liveUses, sampleValues, slugFrom, type MergeField, type TemplateLanguage, type TemplatePlace,
  type TextTemplate,
} from "@/lib/text-templates";
import {
  useCreateTextTemplate, useSaveTextTemplate, useTextTemplate, useTextTemplateActive,
} from "@/lib/text-templates-api";
import { cn, formatDateTime } from "@/lib/utils";
import { useWorkflowCatalog } from "@/lib/workflows-api";

/**
 * One email template (ACL-378, ADR-198 in platform), new or kept: what a workflow's Send email step
 * sends. The English subject and email are needed; a Spanish subject and email are optional and go
 * together, and a lead's language picks which they get. A lead's details go in as fields from the
 * buttons over each box; the preview fills them with a sample lead and shows the email as it
 * leaves, with "Don't want these emails? Reply STOP." under it, which every automated email ends
 * with.
 *
 * As a text template's page: Save sends the `updated_at` it read, the slug is made from the name
 * and fixed after, and a template a published workflow sends cannot be turned off. A text
 * template opened here goes to its own page.
 */
export function EmailTemplatePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const tenantId = params.get("tenant") ? Number(params.get("tenant")) : undefined;
  const isNew = slug === undefined || slug === "new";
  const { data, isPending, error, refetch } = useTextTemplate(isNew ? undefined : slug, tenantId);

  if (!isNew && isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (!isNew && error && !data) {
    return (
      <Page>
        {error instanceof ApiError && error.status === 404 ? (
          <EmptyState
            title="No such template"
            description="It may belong to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/email-templates">All email templates</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            error={error}
            title="Could not load the template"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }
  if (!isNew && data && channelOf(data) !== "email") {
    return <Navigate to={`/text-templates/${encodeURIComponent(data.slug)}${tenantId ? `?tenant=${tenantId}` : ""}`} replace />;
  }
  return <Editor key={data?.slug ?? "new"} template={isNew ? null : (data ?? null)} tenantId={tenantId} />;
}

interface Values {
  name: string;
  subject_en: string;
  en: string;
  subject_es: string;
  es: string;
}

const valuesOf = (t: TextTemplate | null): Values => ({
  name: t?.name ?? "",
  subject_en: t?.subjects?.en ?? "",
  en: t?.texts.en ?? "",
  subject_es: t?.subjects?.es ?? "",
  es: t?.texts.es ?? "",
});
const same = (a: Values, b: Values) => (Object.keys(a) as (keyof Values)[]).every((k) => a[k] === b[k]);
const STALE = "Somebody else saved this template";

function Editor({ template, tenantId }: { template: TextTemplate | null; tenantId?: number }) {
  const navigate = useNavigate();
  const toast = useToast();
  const catalog = useWorkflowCatalog(tenantId);
  const fields: readonly MergeField[] = catalog.data?.merge_fields.length ? catalog.data.merge_fields : MERGE_FIELDS;
  const create = useCreateTextTemplate(tenantId);
  const save = useSaveTextTemplate(tenantId);
  const active = useTextTemplateActive(tenantId);
  const reload = useTextTemplate(template?.slug, tenantId);
  const [baseline, setBaseline] = React.useState(() => valuesOf(template));
  const [values, setValues] = React.useState(baseline);
  const [readAt, setReadAt] = React.useState(template?.updated_at);
  const [tried, setTried] = React.useState(false);
  const [serverProblem, setServerProblem] = React.useState<string | null>(null);
  const [stale, setStale] = React.useState(false);
  const dirty = !same(values, baseline);
  const busy = create.isPending || save.isPending;
  const input = {
    name: values.name,
    texts: { en: values.en, es: values.es || null },
    subjects: { en: values.subject_en, es: values.subject_es || null },
  };
  const problem = emailTemplateProblem(input, fields, { creating: !template });
  // A field written wrong is said at once; a missing name, subject or email only once Save was
  // pressed.
  const shown = problem && (tried || (problem.place !== "name" && values[problem.place].trim() !== "")) ? problem : null;
  const errorAt = (place: TemplatePlace) => (shown?.place === place ? shown.message : undefined);
  const live = template ? liveUses(template) : [];

  // A tab closed or reloaded with changes not kept asks first.
  React.useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = (patch: Partial<Values>) => {
    setServerProblem(null);
    setValues((v) => ({ ...v, ...patch }));
  };

  const onSave = async () => {
    setTried(true);
    if (problem || busy) return;
    const payload = emailTemplatePayload(input);
    try {
      if (!template) {
        const made = await create.mutateAsync({ ...payload, channel: "email", slug: slugFrom(payload.name) });
        toast.success("Template saved", made.name);
        navigate(`/email-templates/${encodeURIComponent(made.slug)}${tenantId ? `?tenant=${tenantId}` : ""}`, { replace: true });
        return;
      }
      const kept = await save.mutateAsync({ ...payload, slug: template.slug, updated_at: readAt });
      const next = valuesOf(kept);
      setBaseline(next);
      setValues(next);
      setReadAt(kept.updated_at);
      setTried(false);
      toast.success(
        "Template saved",
        liveUses(kept).length > 0 ? "Workflows that send it use the new words from now on." : kept.name,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.message.startsWith(STALE)) {
        setStale(true);
        return;
      }
      setServerProblem(e instanceof ApiError ? e.message : "Could not save the template. Please try again.");
    }
  };

  const loadTheirs = async () => {
    const fresh = await reload.refetch();
    // A refetch that failed still hands back the copy cached before: that is not their version.
    if (!fresh.isSuccess) {
      toast.error("Could not load their version", "Please try again.");
      return;
    }
    const theirs = valuesOf(fresh.data);
    setBaseline(theirs);
    setValues(theirs);
    setReadAt(fresh.data.updated_at);
    setStale(false);
    setServerProblem(null);
  };

  const toggle = async (on: boolean) => {
    if (!template) return;
    try {
      const done = await active.mutateAsync({ slug: template.slug, active: on });
      setReadAt(done.updated_at);
      toast.success(on ? "Template turned on" : "Template turned off", done.name);
    } catch (e) {
      toast.error("Could not change the template", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Page>
      <Link to="/email-templates" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Email templates
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-4 pb-4">
        <div className="min-w-0 space-y-1">
          <h1 className="text-xl font-semibold tracking-tight text-text">{template ? template.name : "New email template"}</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
            {template ? (
              <>
                <Badge tone={template.active ? "success" : "neutral"} size="sm">
                  {template.active ? "On" : "Off"}
                </Badge>
                <span title={formatDateTime(template.updated_at)}>
                  Saved {formatDateTime(template.updated_at)}
                  {template.updated_by_name && ` by ${template.updated_by_name}`}
                </span>
              </>
            ) : (
              <span>Written once, sent by any workflow's Send email step that picks it.</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {template && (
            <Tooltip
              content={
                live.length > 0
                  ? `Sent by ${live.map((u) => u.name).join(", ")}. Unpublish it first to turn this off.`
                  : template.active
                    ? "Turn off: no workflow can pick it."
                    : "Turn on: workflows can pick it again."
              }
            >
              <span className="inline-flex items-center gap-2 text-xs text-text-muted">
                <Switch
                  checked={template.active}
                  disabled={active.isPending || (template.active && live.length > 0)}
                  aria-label="Template on"
                  onCheckedChange={(on) => void toggle(on)}
                />
                {template.active ? "On" : "Off"}
              </span>
            </Tooltip>
          )}
          <Button variant="default" disabled={(!dirty && Boolean(template)) || busy} loading={busy} onClick={() => void onSave()}>
            Save
          </Button>
        </div>
      </header>

      <div className="space-y-3 pb-4 empty:hidden">
        {stale && (
          <Callout tone="warning">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              Somebody else saved this template after you opened it, so yours was not saved.
              <Button variant="outline" size="sm" loading={reload.isFetching} onClick={() => void loadTheirs()}>
                Load their version
              </Button>
              <span className="text-text-muted">Your unsaved changes here will be replaced.</span>
            </span>
          </Callout>
        )}
        {serverProblem && <Callout tone="danger">{serverProblem}</Callout>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <Card className="space-y-5 p-5">
            <Field
              label="Name"
              htmlFor="template-name"
              hint={
                template
                  ? `Workflows pick it as "${template.name}", and a lead's history names the email by it. Its id stays ${template.slug}.`
                  : "What admins pick in a workflow, and what a lead's history calls the email."
              }
              error={errorAt("name")}
            >
              <Input
                id="template-name"
                value={values.name}
                maxLength={MAX_NAME_CHARS}
                placeholder="First email after no answer"
                invalid={shown?.place === "name"}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Field>
          </Card>
          {(["en", "es"] as const).map((language) => (
            <Card key={language} className="space-y-4 p-5">
              <div>
                <h2 className="text-sm font-semibold text-text">
                  {LANGUAGE_NAME[language]}
                  {language === "es" && <span className="font-normal text-text-muted"> (optional)</span>}
                </h2>
                <p className="mt-0.5 text-xs text-text-muted">
                  {language === "en"
                    ? "Every lead without a Spanish email gets this one."
                    : "Spanish-speaking leads get this one. Left empty, they get the English email."}{" "}
                  A word after a bar is used when the lead has no value: {"{{first_name|there}}"} says "there".
                </p>
              </div>
              <DetailBox
                id={`template-subject-${language}`}
                label="Subject"
                value={language === "en" ? values.subject_en : values.subject_es}
                fields={fields}
                error={errorAt(language === "en" ? "subject_en" : "subject_es")}
                placeholder={language === "en" ? "{{first_name|Hello}}, about your accident" : "{{first_name|Hola}}, sobre su accidente"}
                onChange={(v) => set(language === "en" ? { subject_en: v } : { subject_es: v })}
              />
              <DetailBox
                id={`template-${language}`}
                label="Email"
                multiline
                value={language === "en" ? values.en : values.es}
                fields={fields}
                error={errorAt(language)}
                placeholder={
                  language === "en"
                    ? "Hi {{first_name|there}},\n\nWe tried to call you about your accident. When is a good time to talk?"
                    : "Hola {{first_name|}},\n\nIntentamos llamarle sobre su accidente. ¿Cuándo es un buen momento para hablar?"
                }
                onChange={(v) => set(language === "en" ? { en: v } : { es: v })}
              />
            </Card>
          ))}

          {template && (
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-text">Used by</h2>
              {template.used_by.length === 0 ? (
                <p className="mt-1 text-xs text-text-muted">No workflow sends it yet. Pick it in a workflow's Send email step.</p>
              ) : (
                <ul className="mt-2 divide-y divide-border">
                  {template.used_by.map((u) => (
                    <li key={u.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <Link to={`/workflows/${u.id}`} className="font-medium text-text underline-offset-4 hover:underline">
                        {u.name}
                      </Link>
                      <Badge tone={u.status === "published" ? "success" : "neutral"} size="sm">
                        {u.status === "published" ? "Published" : u.status === "draft" ? "Draft" : "Archived"}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>

        <Preview values={values} fields={fields} />
      </div>
    </Page>
  );
}

/** A subject or an email, with buttons that put a lead's detail in where the cursor is. */
function DetailBox({
  id, label, value, fields, error, placeholder, multiline = false, onChange,
}: {
  id: string;
  label: string;
  value: string;
  fields: readonly MergeField[];
  error?: string;
  placeholder: string;
  multiline?: boolean;
  onChange: (text: string) => void;
}) {
  // Where the cursor was last, so a detail goes in there and not at the end.
  const caret = React.useRef<[number, number] | null>(null);
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const [start, end] = caret.current ?? [value.length, value.length];
    onChange(value.slice(0, start) + token + value.slice(end));
    const at = start + token.length;
    caret.current = [at, at];
    requestAnimationFrame(() => {
      const el = document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null;
      el?.focus();
      el?.setSelectionRange(at, at);
    });
  };
  const remember = (el: HTMLInputElement | HTMLTextAreaElement) => {
    caret.current = [el.selectionStart ?? value.length, el.selectionEnd ?? value.length];
  };
  return (
    <Field
      label={label}
      htmlFor={id}
      error={error}
      action={
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`Insert a lead's detail into the ${label.toLowerCase()}`}>
          <Braces className="size-3.5 text-text-subtle" aria-hidden />
          {fields.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => insert(f.key)}
              className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-medium text-text-muted transition-colors hover:border-accent-8 hover:text-accent-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40"
            >
              + {f.label}
            </button>
          ))}
        </div>
      }
    >
      {multiline ? (
        <Textarea
          id={id}
          rows={8}
          value={value}
          invalid={Boolean(error)}
          className="font-sans text-sm leading-6"
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onSelect={(e) => remember(e.currentTarget)}
        />
      ) : (
        <Input
          id={id}
          value={value}
          invalid={Boolean(error)}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, " "))}
          onSelect={(e) => remember(e.currentTarget)}
        />
      )}
    </Field>
  );
}

/** The email as a sample lead gets it: their language's subject and words filled, and the STOP
 *  line under them. */
function Preview({ values, fields }: { values: Values; fields: readonly MergeField[] }) {
  const [language, setLanguage] = React.useState<TemplateLanguage>("en");
  const hasSpanish = values.es.trim() !== "";
  const shownLanguage: TemplateLanguage = language === "es" && hasSpanish ? "es" : "en";
  const samples = sampleValues(fields);
  const subject = fillText(shownLanguage === "es" ? values.subject_es : values.subject_en, samples).trim();
  const source = shownLanguage === "es" ? values.es : values.en;
  const body = source.trim() ? asEmailSent(fillText(source, samples), shownLanguage) : "";
  const to = [samples.first_name, samples.last_name].filter(Boolean).join(" ");
  return (
    <div className="lg:sticky lg:top-6 lg:self-start">
      <Card className="space-y-4 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-text">Preview</h2>
          <div className="inline-flex rounded-md border border-border p-0.5" role="radiogroup" aria-label="Preview language">
            {(["en", "es"] as const).map((l) => (
              <button
                key={l}
                type="button"
                role="radio"
                aria-checked={shownLanguage === l}
                disabled={l === "es" && !hasSpanish}
                onClick={() => setLanguage(l)}
                className={cn(
                  "rounded px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                  shownLanguage === l ? "bg-accent-2 text-accent-11" : "text-text-muted hover:text-text",
                )}
              >
                {LANGUAGE_NAME[l]}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border">
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-b border-border bg-surface-sunken px-4 py-3 text-xs">
            <dt className="text-text-muted">To</dt>
            <dd className="truncate text-text">{to || "The lead"}</dd>
            <dt className="text-text-muted">Subject</dt>
            <dd className="truncate font-medium text-text">{subject || <span className="text-text-subtle">No subject yet</span>}</dd>
          </dl>
          <div className="px-4 py-3">
            {body ? (
              <p className="whitespace-pre-wrap break-words text-sm leading-6 text-text">{body}</p>
            ) : (
              <p className="flex items-center gap-2 text-xs text-text-muted">
                <Mail className="size-4" aria-hidden />
                The email shows here as you write it.
              </p>
            )}
          </div>
        </div>
        <p className="text-[11px] text-text-muted">
          Every automated email ends with the STOP line. A lead who replies STOP gets no more workflow emails; a rep can
          still email them.
        </p>

        <div className="border-t border-border pt-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-subtle">Sample lead</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {fields.map((f) => (
              <React.Fragment key={f.key}>
                <dt className="text-text-muted">{f.label}</dt>
                <dd className="text-text">{f.sample}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </Card>
    </div>
  );
}
